import { useEffect } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { X, ExternalLink, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

// Stable reference for react-markdown plugins (avoids re-allocating an array
// on every render, which causes ReactMarkdown to re-parse).
const REMARK_PLUGINS = [remarkGfm];

const STATUS_STYLES = {
    green:        { bg: "bg-emerald-50",  border: "border-emerald-400", text: "text-emerald-700", label: "GREEN" },
    amber:        { bg: "bg-amber-50",    border: "border-amber-400",   text: "text-amber-700",   label: "AMBER" },
    red:          { bg: "bg-rose-50",     border: "border-rose-400",    text: "text-rose-700",    label: "RED" },
    insufficient: { bg: "bg-slate-50",    border: "border-slate-300",   text: "text-slate-600",   label: "INSUFFICIENT DATA" },
};

export default function CueDrawer({ card, annexure, open, onClose }) {
    // Lock body scroll while open + close on Escape
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => e.key === "Escape" && onClose();
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    if (!card) return null;
    const status = STATUS_STYLES[card.status] || STATUS_STYLES.insufficient;
    const sources = (card.sourceNums || []).map((n) => ({ n, ...(annexure[n] || {}) })).filter((s) => s.url);

    return (
        <>
            {/* Backdrop */}
            <div
                onClick={onClose}
                data-testid="dd-cue-drawer-backdrop"
                className={`fixed inset-0 bg-black/30 z-40 transition-opacity duration-200 no-print ${open ? "opacity-100" : "opacity-0 pointer-events-none"}`}
            />

            {/* Drawer */}
            <aside
                data-testid="dd-cue-drawer"
                className={`fixed top-0 right-0 h-screen w-[640px] max-w-[92vw] bg-ciff-paper z-50 shadow-2xl border-l border-ciff-rule transform transition-transform duration-300 no-print ${open ? "translate-x-0" : "translate-x-full"} flex flex-col`}
            >
                {/* Header */}
                <div className="px-7 py-5 border-b border-ciff-rule flex items-start justify-between gap-4 flex-shrink-0">
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-[0.22em] font-mono text-ciff-inkSoft">
                            Cue {card.n} of 11
                        </div>
                        <h2 className="font-heading text-2xl font-bold text-ciff-ink mt-0.5 tracking-tight">
                            {card.dimension}
                        </h2>
                    </div>
                    <Button
                        onClick={onClose}
                        variant="ghost"
                        size="icon"
                        data-testid="dd-cue-drawer-close"
                        className="rounded-sm text-ciff-inkSoft hover:bg-ciff-paper2 -mr-2"
                    >
                        <X className="h-4 w-4" />
                    </Button>
                </div>

                {/* Scroll body */}
                <div className="flex-1 overflow-y-auto px-7 py-6 space-y-6">
                    {/* Status badge */}
                    <div className={`border ${status.border} ${status.bg} px-4 py-3 flex items-center`}>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-ciff-inkSoft">
                                RAG status
                            </div>
                            <div className={`font-heading text-base font-bold mt-0.5 ${status.text}`}>
                                {status.label}
                            </div>
                        </div>
                    </div>

                    {/* In brief */}
                    {card.plainEnglish && (
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.22em] font-mono text-ciff-inkSoft mb-1.5">
                                In brief
                            </div>
                            <div className="report-prose">
                                <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>
                                    {card.plainEnglish}
                                </ReactMarkdown>
                            </div>
                        </div>
                    )}

                    {/* Detail */}
                    {card.detail && (
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.22em] font-mono text-ciff-inkSoft mb-1.5">
                                What the analyst found
                            </div>
                            <div className="report-prose">
                                <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>
                                    {card.detail}
                                </ReactMarkdown>
                            </div>
                        </div>
                    )}

                    {/* Points to consider */}
                    {card.pointsToConsider && (
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.22em] font-mono text-ciff-magenta mb-1.5">
                                Points to consider
                            </div>
                            <div className="report-prose border-l-2 border-ciff-magenta pl-4">
                                <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>
                                    {card.pointsToConsider}
                                </ReactMarkdown>
                            </div>
                        </div>
                    )}

                    {/* Sources */}
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.22em] font-mono text-ciff-inkSoft mb-2">
                            Sources backing this rating ({sources.length})
                        </div>
                        {sources.length === 0 ? (
                            <p className="text-sm text-ciff-inkSoft italic">No sources cited for this cue.</p>
                        ) : (
                            <ul className="space-y-2.5">
                                {sources.map((src) => (
                                    <li key={src.n} data-testid={`dd-cue-source-${src.n}`} className="border border-ciff-rule px-4 py-3 hover:border-ciff-magenta transition-colors duration-150">
                                        <div className="flex items-start gap-3">
                                            <span className="text-[10px] font-mono text-ciff-inkSoft mt-1">[{src.n}]</span>
                                            <div className="min-w-0 flex-1">
                                                <a
                                                    href={src.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-[14px] font-medium text-ciff-magenta hover:text-ciff-magentaDeep block leading-snug break-words"
                                                >
                                                    {src.title || src.url}
                                                    <ExternalLink className="inline h-3 w-3 ml-1 align-baseline" strokeWidth={1.75} />
                                                </a>
                                                {src.description && (
                                                    <p className="text-[12px] text-ciff-inkSoft mt-1 leading-snug">{src.description}</p>
                                                )}
                                            </div>
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>

                    {/* Jump to full section */}
                    <div className="pt-4">
                        <a
                            href={`#section-${cueToSection(card.n)}`}
                            onClick={onClose}
                            className="inline-flex items-center gap-2 text-sm text-ciff-magenta hover:text-ciff-magentaDeep font-medium"
                        >
                            Read the full section in the report
                            <ArrowRight className="h-3.5 w-3.5" strokeWidth={2} />
                        </a>
                    </div>
                </div>
            </aside>
        </>
    );
}

// Map cue number → corresponding body section (Section number in the long report)
function cueToSection(n) {
    const map = { 1: 2, 2: 3, 3: 4, 4: 5, 5: 7, 6: 2, 7: 9, 8: 2, 9: 8, 10: 10, 11: 11 };
    return map[n] || 2;
}
